/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import _sortBy from 'lodash.sortby';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { readOnly, notEmpty } from '@ember/object/computed';
import { isNone } from '@ember/utils';
import { inject as service } from '@ember/service';
import { Entities, MetricTypes, Periods } from 'cio/utils/metrics/common';
import { task } from 'ember-concurrency';
import { isEmpty } from '@ember/utils';
import MetricsService from 'cio/services/metrics';
import DS from 'ember-data';
import { tracked } from '@glimmer/tracking';
import { TrackedLinkMetric, TrackedLinkMetricStat } from 'cio/utils/metrics/models/tracked-link-metric';
import EnvironmentModel from 'cio/models/environment';
import CampaignModel from 'cio/models/campaign';
import { TimeRange } from 'cio/utils/time';

interface CioTrackedLinkMetricTableArgs {
  period: Periods;
  timeRange?: TimeRange;
  metricQueryParams?: Record<string, string>;
  limit?: number;
  isLinkMetricsForWholeCampaign?: boolean;
  onExpand?: () => unknown;
  showEmptyStats?: boolean;
  campaign?: CampaignModel;
  environment?: EnvironmentModel;
}

export default class CioTrackedLinkMetricTable extends Component<CioTrackedLinkMetricTableArgs> {
  @service store!: DS.Store;
  @service metrics!: MetricsService;

  @tracked expanded = false;

  get limit() {
    return this.args.limit ?? 10;
  }

  get periodTotal() {
    return !isEmpty(this.args.period) && this.args.period !== Periods.ALL_TIME;
  }

  @readOnly('fetchMetric.last.isError') failedToLoad!: boolean;
  @readOnly('fetchMetric.last.value') selectedMetric!: TrackedLinkMetric;
  @readOnly('selectedMetric.stats') stats!: TrackedLinkMetricStat[];
  @notEmpty('stats') hasStats!: boolean;

  fetchMetric = task({ drop: true }, async (forceReload = false) => {
    let { period, timeRange } = this.args;

    let entityType: Entities = '' as any;
    let id = '';
    let actionType = undefined;
    if (this.args.metricQueryParams?.campaign_id) {
      entityType = Entities.CAMPAIGN;
      id = this.args.metricQueryParams.campaign_id;
    } else if (this.args.metricQueryParams?.newsletter_id) {
      entityType = Entities.NEWSLETTER;
      id = this.args.metricQueryParams.newsletter_id;
    } else if (this.args.metricQueryParams?.environment_id) {
      entityType = Entities.ENVIRONMENT;
      id = this.args.metricQueryParams.environment_id;
    } else if (this.args.metricQueryParams?.template_id) {
      entityType = Entities.TEMPLATE;
      id = this.args.metricQueryParams.template_id;
    } else if (this.args.metricQueryParams?.action_id) {
      entityType = Entities.ACTION;
      id = this.args.metricQueryParams.action_id;
    }

    if (forceReload) {
      this.metrics.clearMetrics(MetricTypes.TrackedLinkMetric, entityType, id);
    }

    if (this.args.metricQueryParams?.type) {
      actionType = this.args.metricQueryParams.type;
    }

    let metricMeta: Record<string, string> = {};

    if (this.args.campaign) {
      metricMeta.campaignId = this.args.campaign.id;
    }

    if (this.args.environment) {
      metricMeta.environmentId = this.args.environment.id;
    }

    // If no period, default to "days" query
    if (!period) {
      period = Periods.DAYS;
    }

    if (period === Periods.CUSTOM && timeRange?.start && timeRange?.end) {
      return await this.metrics.fetchTrackedLinkMetric(
        entityType,
        id,
        Periods.CUSTOM,
        timeRange,
        actionType,
        metricMeta,
      );
    } else {
      return await this.metrics.fetchTrackedLinkMetric(entityType, id, period, null, actionType, metricMeta);
    }
  });

  get sortedStats() {
    let { stats } = this;

    if (isNone(stats)) {
      return [];
    }

    let sortBy = this.periodTotal ? 'periodTotalClicked' : 'totalClicked';
    return _sortBy(stats, [sortBy]).reverse();
  }

  get displayedStats() {
    let stats = this.sortedStats;

    if (isNone(stats)) {
      return [];
    }

    if (stats.length < this.limit || this.expanded) {
      return stats;
    }

    return stats.slice(0, this.limit);
  }

  get expandable() {
    return this.displayedStats.length < this.stats.length;
  }

  @action
  expand() {
    this.expanded = true;

    if (this.args.onExpand != null) {
      this.args.onExpand();
    }
  }
}
